import type { ComponentType, PropsWithChildren, Dispatch, SetStateAction, ElementType, ReactNode } from 'react'
import React, { createContext, useEffect, useMemo, useState } from 'react'
import { css } from '@linaria/core'
import { SplitFactoryProvider } from '@splitsoftware/splitio-react'
import type { FactoryResult, Flags, StandardAttributes } from '@fs/flags-js'
import { splitioAuthKey } from '@fs/zion-config'
import zionDebug, { debugVerbose as zionDebugVerbose } from '@fs/zion-debug'
import { NoticeLoading } from '@fs/zion-icon'
import { i18n } from '@fs/zion-locale'
import { updateFlagOverrides, getStandardAttributes, updateStandardAttributes } from '@fs/flags-js/react'
import { flagsAreReady } from './flagsJsHelper'
import useLocalStorageFlags from './overrides/useLocalStorageFlags'

const debug = zionDebug('zion:flags:provider')
const debugVerbose = zionDebugVerbose('zion:flags:provider')
const FLAGS_READY_TIMEOUT = 10000 // this matches the split.io default timeout

export type FeatureFlagsContextValue = {
  splitFactory: SplitIO.IBrowserSDK | undefined
  standardAttributes: StandardAttributes
  flagOverrides: Flags
  setFlagOverrides: Dispatch<SetStateAction<Flags>>
  mockFlags?: Flags
  clients: Record<string, SplitIO.IBrowserClient | null>
}

export const FeatureFlagsContext = createContext<FeatureFlagsContextValue | undefined>(undefined)

function FeatureFlagsProviderBase({
  context,
  enableRealtime,
  LoadingFallback,
  children,
}: {
  context: FeatureFlagsContextValue
  LoadingFallback: ElementType
  enableRealtime: boolean
  children: ReactNode
}): JSX.Element {
  const { splitFactory, standardAttributes } = context || {}
  debugVerbose(
    'Rendering FeatureFlagsProviderBase with splitFactory:',
    splitFactory,
    'enableRealtime:',
    enableRealtime,
    'standardAttributes:',
    standardAttributes
  )
  return (
    <FeatureFlagsContext.Provider value={context}>
      {splitFactory ? (
        <SplitFactoryProvider factory={splitFactory} attributes={standardAttributes}>
          <>{children}</>
        </SplitFactoryProvider>
      ) : (
        <LoadingFallback />
      )}
    </FeatureFlagsContext.Provider>
  )
}

/**
 * The FeatureFlagsProvider renders the SplitFactory at the base of the Provider tree.
 * It holds on to the precomputed StandardAttributes, any overridden Attributes created
 * by the FlagOverride user interface, and any localhost Attributes.
 * These are used by the hook to generate the final flag treatment values.
 */
type FeatureFlagsProviderProps = PropsWithChildren<{
  authKey?: string
  mockFlags?: Flags | Promise<Flags>
  enableRealtime?: boolean
  debugSdk?: boolean
  impressionListener?: { logImpression: (impression: object) => void }
  LoadingFallback?: ComponentType
}>

export default function FeatureFlagsProvider({
  authKey = splitioAuthKey,
  mockFlags,
  enableRealtime = true,
  debugSdk,
  LoadingFallback = LoadingSpinner,
  impressionListener,
  children,
}: FeatureFlagsProviderProps): JSX.Element {
  debugVerbose('FeatureFlagsProvider start with:', { mockFlags, enableRealtime, debugSdk })
  const [factoryResult, setFactoryResult] = useState<FactoryResult | undefined>()
  const [standardAttributes, setStandardAttributes] = useState<StandardAttributes | undefined>()
  const [resolvedMockFlags, setResolvedMockFlags] = useState<Flags | undefined>(
    mockFlags instanceof Promise ? undefined : mockFlags
  )
  const [flagOverrides, setFlagOverrides] = useState<Flags>({})
  const { localStorageFlags } = useLocalStorageFlags()

  if (flagOverrides && factoryResult) {
    updateFlagOverrides(flagOverrides)
  }
  useEffect(() => {
    setFlagOverrides(localStorageFlags)
  }, [localStorageFlags])

  useEffect(() => {
    async function init(): Promise<void> {
      let resolvedFlags: Flags
      if (mockFlags instanceof Promise) {
        resolvedFlags = await mockFlags
      } else {
        resolvedFlags = mockFlags
      }
      if (!standardAttributes) {
        if (resolvedFlags) {
          setStandardAttributes({
            anonId: 'mock-anon-id',
            locale: i18n.language,
          })
        } else {
          debug('Resolving standard attributes for language:', i18n.language)
          setStandardAttributes(await getStandardAttributes(i18n.language))
        }
      }
      initFlagsJsFactory(resolvedFlags, flagOverrides, standardAttributes)
        .then((result: FactoryResult) => {
          setFactoryResult(result)
          setResolvedMockFlags(resolvedFlags)
        })
        .catch((err) => console.error('Failed to load Flags factory, flags will return control:', err))
    }
    init()
    function updateLanguage(): void {
      updateStandardAttributes({ locale: i18n.language }).then((standardAttrs) => {
        debug('updateStandardAttributes complete', standardAttrs)
        setStandardAttributes(standardAttrs)
      })
    }
    i18n.on('languageChanged', updateLanguage)
    return () => {
      i18n.off('languageChanged', updateLanguage)
      // TODO: figure out how to call shutdown correctly
      // debug('FeatureFlagsProvider unmounting, Split factory shutdown')
      // shutdown()
    }
  }, [debugSdk, authKey, mockFlags, impressionListener, flagOverrides, standardAttributes])

  const value: FeatureFlagsContextValue = useMemo(
    () => ({
      splitFactory: factoryResult?.factory,
      standardAttributes,
      mockFlags: resolvedMockFlags,
      flagOverrides,
      setFlagOverrides,
      clients: factoryResult?.clients || null,
    }),
    [factoryResult?.factory, factoryResult?.clients, standardAttributes, resolvedMockFlags, flagOverrides]
  )

  debug('Rendering FeatureFlagsProvider with:', value, enableRealtime, children)
  return (
    <FeatureFlagsProviderBase context={value} enableRealtime={enableRealtime} LoadingFallback={LoadingFallback}>
      {children}
    </FeatureFlagsProviderBase>
  )
}

export function FeatureFlagsDocsProvider({
  context,
  enableRealtime,
  children,
}: PropsWithChildren<{
  context: FeatureFlagsContextValue
  enableRealtime: boolean
}>): JSX.Element {
  return (
    <FeatureFlagsProviderBase context={context} enableRealtime={enableRealtime} LoadingFallback={LoadingSpinner}>
      {children}
    </FeatureFlagsProviderBase>
  )
}

async function initFlagsJsFactory(
  mockFlags: Flags | Promise<Flags>,
  flagOverrides: Flags,
  standardAttributes: StandardAttributes = undefined
): Promise<FactoryResult | void> {
  let resolvedMockFlags: Flags | undefined
  if (mockFlags instanceof Promise) {
    resolvedMockFlags = await mockFlags
  } else {
    resolvedMockFlags = mockFlags
  }
  updateFlagOverrides(flagOverrides)
  return Promise.race([
    flagsAreReady(resolvedMockFlags, flagOverrides, standardAttributes),
    new Promise<void>((_resolve, reject) => {
      setTimeout(
        () => reject(new Error('flags-js factory timed out, will render with the control treatment until ready')),
        FLAGS_READY_TIMEOUT
      )
    }),
  ])
}

const center = css`
  position: fixed;
  top: 50%;
  inset-inline-start: 50%;
  transform: translate(-50%, -50%);
`

function LoadingSpinner(): JSX.Element {
  return (
    <div role="alert" data-testid="flagLoadingSpinner" aria-busy="true" className={center}>
      <NoticeLoading size="lg" />
    </div>
  )
}
